import React, { Fragment, useContext, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { useBeforeunload } from 'react-beforeunload';
import { useTranslation } from 'react-i18next';
import JSZip from 'jszip';
import { uniqBy, orderBy } from 'lodash';
import { EiqSelect } from 'eiq-storybook'

import { DATASHEET_FILTER_TAG, DEFAULT_SAVE_STATUS } from 'src/components/JobDatasheet/JobDatasheetForm/constants';
import { DATASHEET_VERSION_QUERY } from 'src/graphql/datasheet/query';
import { DELETE_ATTACHMENT_MUTATION } from 'src/graphql/attachment/mutation';
import { ExpandableTabs } from 'src/components/JobDatasheet/ExpandableTabs';
import { flatToTree } from 'src/utils/form/section';
import { FormPropTypes } from 'src/components/JobDatasheet/propTypes';
import { FormSection } from 'src/components/JobDatasheet/FormSection';
import { 
  generateDefaultDatasheetResults,
  getButtonTextOfFilterBy,
  getNegativeOutcomeResultFamilyItems,
  getQuestionsWithNegativeOutcomeCount,
  getSectionQuestions,
  getSectionResults,
  preFilledDataDisclaimer,
  applyQuestionConditions as applyQuestionConditionsWithGetResult,
  getDependentQuestionsWithRectiedValueOnParentQuestion,
  showResultVerificationSection
} from 'src/components/JobDatasheet/JobDatasheetForm/utils';
import { GET_OBJECT_PRE_SIGNED_URL } from 'src/graphql/attachment/query';
import { getActiveReportReviewNote } from 'src/components/SurveyForm/utils';
import { getValidationErrors, resultUnique } from 'src/components/JobDatasheet/utils';
import { getValidationVariables } from 'src/utils/form/datasheet';
import { InfoOverlay } from 'src/components/JobDatasheet/InfoOverlay';
import { notification } from 'src/components/Notification';
import { ResultFamilySection } from 'src/components/JobDatasheet/ResultFamilySection';
import { ResultVerificationSection } from 'src/components/JobDatasheet/ResultVerificationSection/ResultVerificationSection';
import { saveStatusReducer } from 'src/components/JobDatasheet/JobDatasheetForm/saveStatusReducer';
import { SurveyType } from 'src/utils/data/types';
import { useMutation } from 'src/graphql';
import { UNDER_EXTERNAL_SAQ, UNDER_INTERNAL_SAQ, UNDER_REVIEW, REVIEWER_REVIEW_UNDERWAY, VERIFIER_REVIEW_UNDERWAY } from 'src/utils/data/job';
import client from 'src/utils/api/client';
import ExternalUserRightSideSection from 'src/components/JobDatasheet/ExternalUserRightSideSection';
import Loader from 'src/components/Loader';
import s from 'src/components/JobDatasheet/JobDatasheetForm/JobDatasheetForm.styles';
import SidebarContext from 'src/utils/context/SidebarContext';
import { withJobContext } from 'src/utils/context/JobContext';
import { noop } from 'src/utils/core/event';
import { JobDatasheetLanguageProvider } from 'src/components/JobDatasheet/context/JobDatasheetLanguageContext';

const datasheetNameOfAdditionalReportingDocuments = 'Additional Reporting Documents';

const JobDatasheetFormComponent = ({
  jobContext,
  activeTab,
  countryLanguages,
  datasheetBase,
  datasheetsCache,
  factory,
  setDatasheetsVersions,
  getResultHistories,
  hasSaqService,
  hasUnverifiedSaqService,
  isDAPVisible,
  isUnderReviewing,
  onSave,
  readonly: readonlyReal,
  refetchReportReview,
  refetchResultHistories,
  reportReviewData,
  reportReviewStatistics,
  resultHistories,
  results,
  selectedLanguage,
  setSelectedLanguage,
  setResults,
  storedResult,
  setValidationErrors,
  userIsAuthorizedExternalSaqUser,
  isQuesPresentInMultipleDatasheets,
  setIsDatasheetLoading,
  implementNewSaq,
  currentUserId,  
  isVerifier,
  isAdmin,
  isExternalUser,
  isReviewer,
  hasOpenComment,
  showError,
  setIsSavingDatasheetCommentStatus,
  setIsSaving,
}) => {
  const { t } = useTranslation();

  const {
    job,
    user: {
      isReportReviewer,
      isInternalUser,
    },
    results: {
      isDatasheetResultsLoaded,
    },
  } = jobContext;

  const datasheetVersionId = datasheetBase.currentVersion.id;
  const { showToFactoryUser } = datasheetBase.currentVersion;
  const [loading, setLoading] = useState(false);
  const [datasheet, setDatasheet] = useState(datasheetsCache[datasheetVersionId] || datasheetBase);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [toggleOpenComment, setToggleOpenComment] = useState(false);
  const isJobStatusReviewOrSaq = job.status === UNDER_REVIEW || job.status === UNDER_INTERNAL_SAQ;
  const showManipulationSectionInitially = !readonlyReal && isJobStatusReviewOrSaq;
  const [lastSavedDatasheetQuestionID, setLastSavedDatasheetQuestionID] = useState(0);
  // For new saq implementation, If  'Show to factory user' is false, then that datasheet will be editable by - 
  // 1. SAQ verifier in job status = verifier_review_underway
  // 2. Report reviewer in job status = reviewer_review_underway
  const readonly = (implementNewSaq && !showToFactoryUser) ? 
  !((isVerifier && job.status === VERIFIER_REVIEW_UNDERWAY) || (isReviewer && job.status === REVIEWER_REVIEW_UNDERWAY)) : readonlyReal;

  const requestDatasheet = async (id, versionId) => {
    const result = await client.query({
      query: DATASHEET_VERSION_QUERY,
      variables: { id, versionId },
      fetchPolicy: 'network-only'
    });

    return result?.data ? { id , currentVersion: result?.data?.datasheetVersionEffective } : null;
  };

  useEffect(() => {
    if (!datasheetsCache[datasheetVersionId]) {
      setLoading(true);
      setIsDatasheetLoading(true);
      requestDatasheet(datasheetBase.id, datasheetVersionId).then(response => {
        if (response) {
          const cache = {};
          cache[datasheetVersionId] = response;
          setDatasheetsVersions({ ...cache , ...datasheetsCache });
          setDatasheet(response);
        }
        setLoading(false);
        setIsDatasheetLoading(false);
      });
    }
  }, [datasheetVersionId]);

  const [saveStatus, dispatchSaveStatus] = useReducer(saveStatusReducer, DEFAULT_SAVE_STATUS);
  const { lastSavedQuestions, retrying, saveFailedQuestions } = saveStatus;

  useEffect(() =>{
    if (saveStatus?.savingBlocks[0] === 'result') {
      if (lastSavedDatasheetQuestionID === saveStatus?.removedSavedQuestionId){
        setIsSaving(false);
      } else if (saveStatus && saveStatus?.lastSavedQuestions?.length > 0) {    
        setIsSaving(true);      
      }
    }
  }, [saveStatus])

  const isAdditionalReportingDocuments = datasheet.currentVersion.name === datasheetNameOfAdditionalReportingDocuments;
  const defaultResults = generateDefaultDatasheetResults(datasheet, job);

  const statistic = reportReviewStatistics.find(
    d => d.type === 'datasheet' && +d.typeId === +datasheet.id,
  );
  const { expanded: isSidebarExpanded } = useContext(SidebarContext);

  const [collapsedSections, setCollapsedSections] = useState([]);
  const [currentFilter, setCurrentFilter] = useState(DATASHEET_FILTER_TAG.ALL);
  const [disableDownload, setDisableDownload] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCreateNote, setIsCreateNote] = useState(false);
  const [isInfoCollapsed, setIsInfoCollapsed] = useState(true);
  const [quickJumpOpened, setQuickJumpOpened] = useState(false);

  useEffect(() => {
    setResults(resultUnique((storedResult || { data: [] }).data.concat(defaultResults)));
  }, [
    defaultResults.length, isDatasheetResultsLoaded
  ]);

  useEffect(() => {
    if (readonly || !isDatasheetResultsLoaded || (defaultResults.length === 0)) {
      return;
    }

    const storedResultsData = (storedResult || { data: [] }).data;
    const combinedResults = resultUnique(storedResultsData.concat(defaultResults));
    const storedQuestionIds = storedResultsData.map(c => c.datasheetQuestionId);

    // eslint-disable-next-line consistent-return
    combinedResults.forEach(question => {
      if ((question.result !== null) && !storedQuestionIds.includes(question.datasheetQuestionId)) {
        return (question.result.type === 'autofill' && question.isAutofill === false) ? onSave({ ...question, isAutofill: true }, noop) : onSave(question, noop);
      }
    });
  }, [
    defaultResults.length,
    isDatasheetResultsLoaded,
    readonly,
  ]);

  const [selectedQuestion, setSelectedQuestion] = useState();
  const [showHistory, setShowHistory] = useState(false);
  const [timer, setTimer] = useState([]);

  useBeforeunload((event) => {
    if (saveFailedQuestions?.length > 0) {
      event.preventDefault();
    }
  });
  
  const activeSection = selectedQuestion
    ? datasheet.currentVersion.datasheetSections.find(section => section.position === selectedQuestion.section)
    : null;
  const activeQuestion = activeSection
    ? activeSection.questions.find(question => question.position === selectedQuestion.question)
    : null;

  const activeReportReviewNote = getActiveReportReviewNote(reportReviewData, activeQuestion);

  const [showNote, setShowNote] = useState(!!activeReportReviewNote);
  const [showResult, setShowResult] = useState(!activeReportReviewNote);

 const onChangeSelectedQuestion = (questionNo) =>{
  let foundSectionIndex = -1;
  let foundQuestionIndex = -1;
  const {datasheetSections} = datasheet.currentVersion;
    for (let i = 0; i < datasheetSections.length; i+=1){
      const questions = datasheetSections[i]?.questions;        
      const indexOfQuestion = questions.findIndex(ques => ques.id === questionNo);
      if (indexOfQuestion !== -1) {
        foundSectionIndex = i;
        foundQuestionIndex = questions[indexOfQuestion].position;
        break;
      } 
    };
  setSelectedQuestion({ section: foundSectionIndex, question: foundQuestionIndex });
 }

  const setSaveResult = (data, isSuccess) => {
    dispatchSaveStatus({ type: 'SET_SAVED_RESULT', result: isSuccess, datasheetQuestionId: data.datasheetQuestionId });
  };

  const sections = loading ? [] : flatToTree(
    datasheet.currentVersion.datasheetSections || [],
    null,
    SurveyType.Datasheet,
  );

  const allRectifiedSections = [];
  const datasheetResultFamily = datasheet.currentVersion.defaultResultFamily;
  const negativeOutComeResultFamilyItems = useMemo(() => getNegativeOutcomeResultFamilyItems(datasheet), [datasheet]);

  const negativeOutComeResultIds = useMemo(() => negativeOutComeResultFamilyItems.map(outcome => outcome.resultCategoryId) || null
    , [negativeOutComeResultFamilyItems]);

  const getResult = question => results.find(
    v => v.datasheetQuestionId === question?.datasheetQuestionId,
  ) || {};

  // need to pass 'true' as last parameter for recified result questions
  const applyQuestionConditionsWithRectifiedAns = section => applyQuestionConditionsWithGetResult(section, getResult, datasheet, true, true);
 
  let resultsChanged = [];
  
  
  let allQuestionsResults = [];
  const getAllQuestions = (sectionsArr) => {    
    for (let i = 0; i < sectionsArr.length; i+=1) {
      const section = sectionsArr[i];
      for(let q = 0; q < section.questions.length; q+=1){
        allQuestionsResults.push(section.questions[q]);
      }
      if(section.children.length > 0){
        getAllQuestions(section.children);
      }
    }

  }

  const setResult = async (question, field, value, shouldShowError = true, rectifiedValue = false) => {
    if(!isInternalUser && !isVerifier){
      if(!(isExternalUser && readonly) && (field !== 'attachments' || field !== 'attachmentsMeta') ){
        if (readonly && shouldShowError) {
          notification.error(readonly);
          return;
        }
        if (readonly) {
          return;
        }
      }
    }
    const newResults = resultsChanged.length !== 0 && resultsChanged.length !== results.length ? resultsChanged : [...results];
    let index = newResults.findIndex(v => v.datasheetQuestionId === question.datasheetQuestionId);

    if (index !== -1) {
      const prefilledValue = newResults[index]?.isPrefilled;
      newResults[index][field] = value;
      newResults[index].isPrefilled = false;
      newResults[index].isAutofill = false;

      if ( value && value.type === 'autofill' ) {
        newResults[index].isAutofill = true;
        newResults[index].isPrefilled = prefilledValue;
      }
    } else {
      const indexInDefault = defaultResults.findIndex(v => v.datasheetQuestionId === question.datasheetQuestionId);
      if (indexInDefault !== -1) {
        newResults.push({
          ...defaultResults[indexInDefault],
          [field]: value,
        });
      }
      index = newResults.findIndex(v => v.datasheetQuestionId === question.datasheetQuestionId);
    }
    resultsChanged = newResults;

    const outcomes =
    ((datasheetResultFamily || {}).currentVersion || {})
      .resultFamilyOutcomes || [];
    const positiveOutcome =
  outcomes.find(
    v => v.resultCategory.currentVersion.name === 'Positive Outcome',
  ) || {};
    const negativeOutcomes =
      outcomes.filter(
        v => v.resultCategory.currentVersion.name === 'Negative Outcome',
      ) || {};  

    const outcome = field === 'validationResultId' ? value : newResults[index]?.validationResultId; 
    const negativeOutcome = negativeOutcomes.find(item => item.resultId === outcome);

    if(field === 'result'){
       setIsSaving(true);
       setLastSavedDatasheetQuestionID(question.datasheetQuestionId);
    }
    const questionResultId = getResult(question).id;
    
    await setResults(newResults);
    await dispatchSaveStatus({ type: 'ADD_SAVING_BLOCK', block: field });
    await onSave(newResults[index], setSaveResult, false, true);
    await dispatchSaveStatus({ type: 'REMOVE_LAST_SAVED_QUESTION', datasheetQuestionId: question.datasheetQuestionId });
    
    if (questionResultId) {
      getResultHistories({ variables: { entityId: questionResultId, entityClass: 'DatasheetQuestionResult' } });
    }

    if(rectifiedValue && field === 'result'){
       
      allQuestionsResults = [];  

      getAllQuestions(sections);
      const {datasheetQuestionId} = activeQuestion;
      const dependentQuestions = getDependentQuestionsWithRectiedValueOnParentQuestion((allQuestionsResults || []).map(c => c).flat(1), datasheetQuestionId, activeQuestion, newResults[index]?.result);

      for (let i = 0; i < dependentQuestions.length; i+=1){
        const dependenQuestion = dependentQuestions[i];   
        let dependenQuestionResult = getResult(dependenQuestion).result; 
        if(dependenQuestionResult){
          dependenQuestionResult.rectifiedValue = null;
          if((field === 'validationResultId' || field === 'result') && outcome === negativeOutcome.resultId){
            dependenQuestionResult.validationResultId = negativeOutcome.resultId;
            setResult(dependenQuestion, 'result', dependenQuestionResult, false);   
            setResult(dependenQuestion, 'validationResultId', negativeOutcome.resultId, false);            
          }
       }else{
          const validationResultIdValue = outcome === negativeOutcome.resultId ? negativeOutcome.resultId : null;
          if(validationResultIdValue){
            if(dependenQuestion?.datasheetQuestion?.currentVersion?.questionType === 'table' ){
              const columns = (dependenQuestion?.datasheetQuestion?.currentVersion?.tableSettings.columns || []).map(column => ({
                type: column.type,
                column: column.name
              }));
              dependenQuestionResult = null; 
            }
            else 
              dependenQuestionResult = {type: dependenQuestion?.datasheetQuestion?.currentVersion?.questionType, validationResultId: validationResultIdValue, rectifiedValue: null};
            
            setResult(dependenQuestion, 'validationResultId', validationResultIdValue, false);
            setResult(dependenQuestion, 'result', dependenQuestionResult, false);   
          }
        }
       
      }

    }
  };

  const isDatasheetPrefilled = results.some((rs) => rs.isPrefilled === true);

  const applyQuestionConditions = section => applyQuestionConditionsWithGetResult(section, getResult, datasheet, true, true);

  useEffect(() => {
    if (!activeTab) {
      dispatchSaveStatus({ type: 'CLEAR_LAST_SAVED_QUESTION' });
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeQuestion) {
      const questionResultId = getResult(activeQuestion).id;
      if (questionResultId) {
       getResultHistories({ variables: { entityId: questionResultId, entityClass: 'DatasheetQuestionResult' } });
      }
    }
  }, [activeQuestion]);

  useEffect(() => {
    if(selectedQuestion) {
      if(activeReportReviewNote && activeReportReviewNote.closedAt) {
        setShowResult(true);
        setShowNote(false);
      }
    }
  }, [selectedQuestion]);

  const toggleSection = id => {
    const newCollapsedSections = [...collapsedSections];
    if (collapsedSections.indexOf(id) === -1) {
      newCollapsedSections.push(id);
    } else {
      newCollapsedSections.splice(newCollapsedSections.indexOf(id), 1);
    }
    setCollapsedSections(newCollapsedSections);
  };

  const toggleSelectedQuestion = (section, question) => {
    const currentSection = section
    ? datasheet.currentVersion.datasheetSections.find(sec => sec.position === section)
    : null;
    const currentQuestion = currentSection
    ? currentSection.questions.find(q => q.position === question)
    : null;
    if (implementNewSaq && userIsAuthorizedExternalSaqUser && !getResult(currentQuestion)?.commentStatus) {
      setIsInfoCollapsed(true);
      setSelectedQuestion({});
    } else { 
      setSelectedQuestion({
        section,
        question,
      });
    }
  }

  const isBlockSaveFailed = datasheetQuestionId => saveFailedQuestions.indexOf(datasheetQuestionId) !== -1;
  const isBlockSaveSuccess = datasheetQuestionId => lastSavedQuestions.indexOf(datasheetQuestionId) !== -1;

  const retryAutoSave = datasheetQuestionIds => {
    const newResults = [...results];

    let firstResult = true;
    if (datasheetQuestionIds && datasheetQuestionIds.length > 0) {
      datasheetQuestionIds.forEach(id => {
        const index = newResults.findIndex(
          v => v.datasheetQuestionId === id,
        );

        if (timer[index]) {
          clearTimeout(timer[index]);
        }
        timer[index] = setTimeout(() => {
          onSave(newResults[index], setSaveResult, firstResult);
          dispatchSaveStatus({ type: 'REMOVE_LAST_SAVED_QUESTION', datasheetQuestionId: id });
          if (firstResult) firstResult = false;
        }, 1500);
      });
      setTimer(timer);
    }
    dispatchSaveStatus({ type: 'RETRY_AUTO_SAVE' });
  };

  const filterTags = Object.values(DATASHEET_FILTER_TAG);

  const sectionRefs = [...Array(1000).keys()].map(() => useRef(null));

  let sectionOptionIndex = 0;
  const getNextSectionRef = () => {
    const ref = sectionRefs[sectionOptionIndex];
    sectionOptionIndex += 1;
    return ref;
  };

  const getSectionOptions = (sectionsArr, indexPath = []) => sectionsArr.map(v => ([
    {
      label: `${[...indexPath, v.position + 1].join('.')}. ${v.name}`,
      level: indexPath.length,
      ref: getNextSectionRef(),
      value: v.originalIndex,
    },
    ...getSectionOptions(v.children, [...indexPath, v.position + 1]).flat(),
  ]));

  const sectionsOptions = getSectionOptions(
    flatToTree(
      datasheet.currentVersion.datasheetSections || [],
      null,
      SurveyType.Datasheet
    ),
  ).flat();

  const sectionsWrapper = useRef(null);

  useLayoutEffect(() => {
    setIsCreateNote(false);
  }, [activeQuestion]);

  const addNote = () => setIsCreateNote(true);

  useLayoutEffect(() => {
    if (isInternalUser) {
      setShowResult(!activeReportReviewNote);
      setShowNote(!!activeReportReviewNote);
    } else {
      setShowResult(false);
      setShowNote(true);
    }
    setShowHistory(false);
  }, [activeReportReviewNote]);

  const activeReportReviewComments = activeReportReviewNote && activeReportReviewNote.reportReviewComments;

  const getResultAttachments = question => {
    const result = getResult(question);

    if (result.attachmentsMeta && result.attachmentsMeta.length > 0) {
      return result.attachmentsMeta || [];
    }

    return (result.attachments || []).map(x => x.attachmentId);
  };

  const getResultExternalAttachments = question => {
    const result = getResult(question);

    if (result.externalAttachmentsMeta && result.externalAttachmentsMeta.length > 0) {
      return result.externalAttachmentsMeta || [];
    }

    return (result.externalAttachments || []).map(x => x.attachmentId);
  };

  const { defaultResultFamilyId } = datasheet.currentVersion;
  /**
   * hides negative outcome button for datasheets without result family
   * or in case user is not an internal-user type
   */
  if (!defaultResultFamilyId || !isInternalUser) {
    const negativeOutcomeIndex = filterTags.findIndex(tag => tag === DATASHEET_FILTER_TAG.NEGATIVE_OUTCOME);
    if (negativeOutcomeIndex !== -1) {
      filterTags.splice(negativeOutcomeIndex, 1);
    }
  }

  const getAttachmentData = () => {
    if (!isAdditionalReportingDocuments || !datasheet.currentVersion.datasheetSections) {
      return [];
    }
    const section = datasheet.currentVersion.datasheetSections[0];
    const questions = getSectionQuestions(
      section,
      applyQuestionConditions(section),
      currentFilter,
      statistic
    );

    const questionResults = questions.map((question) => getResult(question));

    if (!questionResults[0]?.result) {
      return [];
    }

    return questionResults[0]?.result.rows.reduce((acc, v) => {
      if (v[1].type === 'file') {
        acc.push(v[1].value);
      }

      return acc;
    }, []).flat();
  };

  const attachmentData = getAttachmentData();

  useEffect(() => {
    if (isAdditionalReportingDocuments && datasheet.currentVersion.datasheetSections && attachmentData.length !== 0) {
      setDisableDownload(false);
    }
  }, [isAdditionalReportingDocuments, datasheet.currentVersion.datasheetSections, attachmentData.length]);

  const downloadAttachments = async () => {
    if (!isAdditionalReportingDocuments) {
      return false;
    }
    const getPreSignedUrl = async (id, realName) => {
      const response = await client.query({
        query: GET_OBJECT_PRE_SIGNED_URL,
        variables: realName ? {
          id,
          realName,
        } : { id },
      });
      return response.data.getObjectPreSignedUrl.url;
    };

    const dataWithBlob = await Promise.all(
      getAttachmentData().map(
        async (attachment) => {
          const url = await getPreSignedUrl(attachment.attId, attachment.name);
          const response = await fetch(url);
          const blob = await response.blob();
          return { ...attachment, blob };
        },
      ));

    const zip = new JSZip();
    const folder = zip.folder('collection');

    dataWithBlob.forEach(d => {
      folder.file(d.name, d.blob);
    });
    const content = await folder.generateAsync({ type: 'blob' });

    saveAs(content, job.reportNumber);

    setDisableDownload(false);

    return true;
  };

  const onClickDownloadAttachments = e => {
    e.preventDefault();
    e.stopPropagation();

    setDisableDownload(true);

    downloadAttachments();
  };

  const datasheetCurrentVersionId = datasheet.currentVersion.id;
  const datasheetId = datasheet.id;
  const activeSectionResults = activeSection ? getSectionResults(activeSection, results) : [];
  const activeSectionVariables = activeSectionResults ? getValidationVariables(activeSectionResults) : [];

  const [negativeOutcomeQuestionCount, setNegativeOutcomeQuestionCount] = useState(0);
  useEffect(() => {
    let count = 0;
    (datasheet.currentVersion.datasheetSections || []).forEach((section) => {
      const questions = getSectionQuestions(
        section,
        applyQuestionConditions(section),
        DATASHEET_FILTER_TAG.ALL,
        statistic,
      );
      count += getQuestionsWithNegativeOutcomeCount(questions, negativeOutComeResultIds, getResult);
    });
    setNegativeOutcomeQuestionCount(count);
  });

  useEffect(() => {
    if (!showManipulationSectionInitially) {
      return;
    }
  
    // show blank section if all the sections are collapsed
    if (isCollapsed) {
      setSelectedQuestion({});
      return;
    }
  
    const sectionsArr = datasheet?.currentVersion?.datasheetSections || [];
    let foundSectionIndex = -1;
    let foundQuestionIndex = -1;
  
    for (let i = 0; i < sectionsArr.length; i+=1) {
      const section = sectionsArr[i];
      if (section.questions && section.questions.length > 0) {
        const questions = getSectionQuestions(
          section,
          applyQuestionConditions(section),
          currentFilter,
          statistic,
          negativeOutComeResultIds,
          getResult
        );
  
        const indexOfQuestion = section.questions.findIndex(ques => ques === questions[0]);
  
        if (indexOfQuestion !== -1) {
          foundSectionIndex = i;
          foundQuestionIndex = indexOfQuestion;
          break;
        }
      }
    }
  
    if (foundSectionIndex !== -1) {
      setSelectedQuestion({ section: foundSectionIndex, question: foundQuestionIndex });
      setIsInfoCollapsed(false);
    } else {
      setSelectedQuestion({});
    }
  }, [readonly, currentFilter, isCollapsed, JSON.stringify(datasheet), job.status]);

  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT_MUTATION);
  const onDeletingExternalAttachment = async (filename, attId) => {
    if (filename !== 'loading.file' && attId) {
      setResult(
        activeQuestion,
        'externalAttachments',
        (getResult(activeQuestion).externalAttachments || [])
          .filter(({ attachmentId }) => String(attachmentId) !== String(attId)));

      setResult(
        activeQuestion,
        'externalAttachmentsMeta',
        (getResult(activeQuestion).externalAttachmentsMeta || [])
          .filter(({ attId: attachmentId }) => String(attachmentId) !== String(attId)));

      await deleteAttachment({
        variables: {
          id: attId,
        },
      });
    }
  };

  const onDeletingAttachment = async (filename, attId) => {
    if (filename !== 'loading.file' && attId) {
      setResult(
        activeQuestion,
        'attachments',
        (getResult(activeQuestion).attachments || [])
          .filter(({ attachmentId }) => String(attachmentId) !== String(attId)));

      setResult(
        activeQuestion,
        'attachmentsMeta',
        (getResult(activeQuestion).attachmentsMeta || [])
          .filter(({ attId: attachmentId }) => String(attachmentId) !== String(attId)));

      await deleteAttachment({
        variables: {
          id: attId,
        },
      });
    }
  };

  const [viewingExternalUserMoreInfo, setViewingExternalUserMoreInfo] = useState(false);

  const hasOpenNoteForExternalUser = useMemo(() => activeReportReviewNote && activeReportReviewNote.isExternal && !activeReportReviewNote.closedAt, [activeReportReviewNote]);

  let isInfoExpanded = !!isDAPVisible && (!isInfoCollapsed || !!defaultResultFamilyId) && (!!activeQuestion || showManipulationSectionInitially);
  if (implementNewSaq && !!defaultResultFamilyId ) {
    isInfoExpanded = showResultVerificationSection(isExternalUser, isVerifier, isReviewer, isAdmin, job.status, hasOpenComment);
  }
  const [isInfoExpandedForExternal, setIsInfoExpandedForExternal] = useState(false);

  useEffect(() => {
    if (activeQuestion && !isInternalUser && !!defaultResultFamilyId) {
      if (hasOpenNoteForExternalUser) {
        setShowNote(true);
        setIsInfoCollapsed(false);
      } else {
        setShowNote(false);
        setIsInfoCollapsed(true);
      }
    }
  }, [activeQuestion]);

  useEffect(() => {
    if (!isInternalUser) {
      if (!isInfoCollapsed) {
        setIsInfoExpandedForExternal(true);
      } else {
        setIsInfoExpandedForExternal(false);
      }
    }
  }, [isInfoCollapsed]);

  const getNumberOfAttachments = (question) => {
    if (!question) {
      return 0;
    }
    return (getResultAttachments(question)?.length || 0) + (getResultExternalAttachments(question)?.length || 0);
  };

  let allQuestionsLength = 0;
  let resultWithOpenStatusCount = 0;
  
  const getRectifiedValueArr = (sectionsArr) => {
    
    for (let i = 0; i < sectionsArr.length; i+=1) {
      const section = sectionsArr[i];
      const questions = getSectionQuestions(
        section,
        applyQuestionConditionsWithRectifiedAns(section),
        currentFilter,
        statistic,
        negativeOutComeResultIds,
        getResult
      );
      if(section.children.length > 0){
        getRectifiedValueArr(section.children);
      }
      if(section.children.length === 0) allRectifiedSections.push(questions);
      allQuestionsLength += questions.length;
      resultWithOpenStatusCount += questions.reduce((acc, question) => {
        const result = getResult(question);
        return acc + (result.commentStatus === 'open');
      }, 0);
    }

  }
  getRectifiedValueArr(sections);
 
  const validationSections = loading ? [] : datasheet.currentVersion.datasheetSections || [];  
  const validationErrors = validationSections.map(section => {
    const sectionResults = getSectionResults(section, results);
    const variables = getValidationVariables(sectionResults);    
    const availableQuestions = applyQuestionConditions(section);
    return availableQuestions.map(question => {
      const questionValidationErrors = getValidationErrors(question, variables, section, results);
      if (questionValidationErrors.length === 0) {
        return null;
      }

      return {
        text: question?.datasheetQuestion?.currentVersion?.text,
        errors: questionValidationErrors,
      };
    }).filter(c => c !== null);
  }).flat(1);
  useEffect(() => {
    if (setValidationErrors) {
      setValidationErrors(validationErrors);
    }
  }, [validationErrors.map(c => c.text).join(',')]);

  const getDatasheetLanguages = () => {
    const datasheetLanguages = (datasheetsCache[datasheetVersionId].currentVersion.languages || []).map(c => ({
      value: c.id,
      label: c.name,
    }));

    return datasheetLanguages;
  };

  const getLanguagesAvailable = (datasheetLanguages) => {
    const languagesAvailable = orderBy(uniqBy([
      {
        value: null,
        label: 'English',
      },
      ...(datasheetLanguages.filter(dl => countryLanguages.some(cl => cl.value === dl.value))),
    ], 'value'), [language => {
      return (language.value === null) ? '' : language.label;
    }], ['asc']);

    return languagesAvailable;
  };

  useEffect(() => {
    if(datasheetsCache[datasheetVersionId]) {
      if(countryLanguages.length === 0) {
        setSelectedLanguage(null);
      } else {
        const datasheetLanguages = getDatasheetLanguages();
        const languagesAvailable = getLanguagesAvailable(datasheetLanguages);
        const languageAvailable = languagesAvailable.find(c => c.value === selectedLanguage);

        setAvailableLanguages(languagesAvailable);
        if(!languageAvailable) {
          setSelectedLanguage(null);
        }
      }
    }
  }, [JSON.stringify(datasheetsCache[datasheetVersionId])]);

  useEffect(() => {
    if (isExternalUser || toggleOpenComment) {
      const filteredRectifiedSections = allRectifiedSections.map(c => c.filter(question => {
        const result = getResult(question);
        return result.commentStatus === 'open';
      }));

      const allQuestions = (filteredRectifiedSections || []).map(c => c).flat(1);
      if (allQuestions.length) onChangeSelectedQuestion(allQuestions[0].id);
    } else {
      const allQuestions = allRectifiedSections[0] || [];
      if (allQuestions.length) onChangeSelectedQuestion(allQuestions[0].id);
    }
  }, [toggleOpenComment]);

  const content = (
    <JobDatasheetLanguageProvider value={{ selectedLanguage, availableLanguages }}>
      <s.Wrapper readOnly={!!readonly}>
        <s.Left
          isFullSize={true}
          isInfoExpanded={isInfoExpanded}
          isInfoExpandedForExternal={isInfoExpandedForExternal}
          isSidebarExpanded={isSidebarExpanded}
          implementNewSaq={implementNewSaq}
        >
          <s.Title>
            {(availableLanguages.length > 1) && (
              <s.LanguageSelectWrapper>
                <EiqSelect
                  options={availableLanguages}
                  onChange={(_name, option) => setSelectedLanguage(option.value)}
                  value={availableLanguages.find(c => c.value === selectedLanguage)}
                  hasChips={true}
                  isSearchEnable={false}
                  backspaceRemovesValue={false}
                  placeholder=''
                  isClearable={false}
                  wrapperClass='language-select-wrapper'
                />
              </s.LanguageSelectWrapper>
            )}
          </s.Title>
          <s.IndicateRequired>{t('INDICATES_REQUIRED_FIELD')}</s.IndicateRequired>
          {isExternalUser && implementNewSaq && showError && <s.ErrorNote>{t('PLEASE_CORRECT_THE_ERRORS')}</s.ErrorNote>}
          {isAdditionalReportingDocuments && (
            <s.DownloadButton disabled={disableDownload} onClick={onClickDownloadAttachments}>
              {t('DOWNLOAD_ALL')}
            </s.DownloadButton>
          )}
          { isDatasheetPrefilled && 
            <s.Disclaimer>{preFilledDataDisclaimer()}</s.Disclaimer> 
          }          
          {!implementNewSaq && <s.MenuWrapper>
            {filterTags.map(tag => (
              <s.GreySwitchButton
                key={tag}
                onClick={() => setCurrentFilter(tag)}
                selected={currentFilter === tag}
                small
              >
                {getButtonTextOfFilterBy(tag, statistic, negativeOutcomeQuestionCount)}
              </s.GreySwitchButton>
            ))}
          </s.MenuWrapper>
          }
          <s.SectionsWrapper ref={sectionsWrapper} fromJob={true} readOnly={!!readonly}>
            {sections.map(
              section => {
                const sectionResults = getSectionResults(section, results);
                const variables = getValidationVariables(sectionResults);

                return (
                  <FormSection
                    key={section.originalIndex}
                    activeQuestion={activeQuestion}
                    applyQuestionConditions={applyQuestionConditions}
                    collapsedSections={collapsedSections}
                    isInfoExpanded={isInfoExpanded}
                    currentFilter={currentFilter}
                    fromJob={true}
                    getNumberOfAttachments={getNumberOfAttachments}
                    getResult={getResult}
                    results={results}
                    getValidationErrors={question => getValidationErrors(question, variables)}
                    isAdditionalReportingDocuments={isAdditionalReportingDocuments}
                    isBlockSaveFailed={isBlockSaveFailed}
                    isBlockSaveSuccess={isBlockSaveSuccess}
                    isQuesPresentInMultipleDatasheets={isQuesPresentInMultipleDatasheets}
                    job={job}
                    negativeOutComeResultIds={negativeOutComeResultIds}
                    readonly={readonly}
                    section={section}
                    allSections={sections}
                    sectionsOptions={sectionsOptions}
                    selectedQuestion={selectedQuestion}
                    setResult={setResult}
                    statistic={statistic}
                    toggleSection={toggleSection}
                    toggleSelectedQuestion={toggleSelectedQuestion}
                    wrapperRef={sectionsOptions.find(c => c.value === section.originalIndex).ref}
                    isInternalUser={isInternalUser}
                    toggleOpenComment={toggleOpenComment}
                    showError={showError}
                  />
                );
              },
            )}
          </s.SectionsWrapper>
        </s.Left>
        {!defaultResultFamilyId && isInternalUser && (
          <s.Right>
            <ExpandableTabs
              retryAutoSave={retryAutoSave}
              retrying={retrying}
              saveFailedQuestions={saveFailedQuestions}
              setIsInfoCollapsed={setIsInfoCollapsed}
              setShowHistory={setShowHistory}
              setShowNote={setShowNote}
              setShowResult={setShowResult}
            />
          </s.Right>
        )}
        {isInfoExpanded && (
          <Fragment>
            {(defaultResultFamilyId && datasheetResultFamily && implementNewSaq) &&
              <ResultVerificationSection
                activeQuestion={activeQuestion}
                activeReportReviewComments={activeReportReviewComments}
                activeReportReviewNote={activeReportReviewNote}
                activeSection={activeSection}
                addNote={addNote}
                currentResult={results.find(result => result?.datasheetQuestionId === activeQuestion?.datasheetQuestionId)}
                storedResult={storedResult}
                defaultResults={defaultResults}
                results={results}
                datasheetCurrentVersionId={datasheetCurrentVersionId}
                datasheetId={datasheetId}
                datasheetResultFamily={datasheetResultFamily}
                getResult={getResult}
                getResultAttachments={getResultAttachments}
                getResultExternalAttachments={getResultExternalAttachments}
                getValidationErrors={() => getValidationErrors(activeQuestion, activeSectionVariables)}
                hasSaqService={hasSaqService}
                hasUnverifiedSaqService={hasUnverifiedSaqService}
                isBlockSaveFailed={isBlockSaveFailed}
                isBlockSaveSuccess={isBlockSaveSuccess}
                isReportReviewer={isReportReviewer}
                isSidebarExpanded={isSidebarExpanded}
                isUnderReviewing={isUnderReviewing}
                jobId={job.id}
                onDeletingAttachment={onDeletingAttachment}
                onDeletingExternalAttachment={onDeletingExternalAttachment}
                readonly={isVerifier ? false : ''}
                refetchReportReview={refetchReportReview}
                reportReviewStatistics={reportReviewStatistics}
                resultHistories={resultHistories}
                selectedQuestion={selectedQuestion}
                setIsCreateNote={setIsCreateNote}
                setResult={setResult}
                setShowHistory={setShowHistory}
                setShowNote={setShowNote}
                showHistory={showHistory}
                showNote={showNote}
                showResult={showResult}
                statistic={statistic}
                allRectifiedSections={isExternalUser || toggleOpenComment ? allRectifiedSections.map(c => c.filter(question => {
                  const result = getResult(question);
                  return result.commentStatus === 'open';
                })) : allRectifiedSections}
                allSections={sections}
                allQuestionsLength={isExternalUser || toggleOpenComment ? resultWithOpenStatusCount : allQuestionsLength}
                onChangeQuestion={onChangeSelectedQuestion}
                toggleOpenComment={toggleOpenComment}
                setToggleOpenComment={setToggleOpenComment}
                resultWithOpenStatusCount={resultWithOpenStatusCount}
                currentUserId={currentUserId}  
                isVerifier={isVerifier}
                isAdmin={isAdmin}
                isInfoExpanded={isInfoExpanded}
                isExternalUser={isExternalUser}
                userIsAuthorizedExternalSaqUser={userIsAuthorizedExternalSaqUser}
                isReviewer={isReviewer}
                setIsSavingDatasheetCommentStatus={setIsSavingDatasheetCommentStatus}
              />
            }
            {!implementNewSaq && (defaultResultFamilyId && datasheetResultFamily  ? 
              <ResultFamilySection
                activeQuestion={activeQuestion}
                activeReportReviewComments={activeReportReviewComments}
                activeReportReviewNote={activeReportReviewNote}
                activeSection={activeSection}
                addNote={addNote}
                currentResult={results.find(result => result?.datasheetQuestionId === activeQuestion?.datasheetQuestionId)}
                datasheetCurrentVersionId={datasheetCurrentVersionId}
                datasheetId={datasheetId}
                datasheetResultFamily={datasheetResultFamily}
                dispatchSaveStatus={dispatchSaveStatus}
                factory={factory}
                getResult={getResult}
                getResultAttachments={getResultAttachments}
                getResultExternalAttachments={getResultExternalAttachments}
                getValidationErrors={() => getValidationErrors(activeQuestion, activeSectionVariables)}
                hasSaqService={hasSaqService}
                hasUnverifiedSaqService={hasUnverifiedSaqService}
                isBlockSaveFailed={isBlockSaveFailed}
                isBlockSaveSuccess={isBlockSaveSuccess}
                isCreateNote={isCreateNote}
                isReportReviewer={isReportReviewer}
                isSidebarExpanded={isSidebarExpanded}
                isUnderReviewing={isUnderReviewing}
                jobId={job.id}
                onDeletingAttachment={onDeletingAttachment}
                onDeletingExternalAttachment={onDeletingExternalAttachment}
                readonly={readonly}
                refetchReportReview={refetchReportReview}
                reportReviewStatistics={reportReviewStatistics}
                resultHistories={resultHistories}
                selectedQuestion={selectedQuestion}
                setIsCreateNote={setIsCreateNote}
                setResult={setResult}
                setShowHistory={setShowHistory}
                setShowNote={setShowNote}
                setShowResult={setShowResult}
                showHistory={showHistory}
                showNote={showNote}
                showResult={showResult}
                statistic={statistic}   />
             : (
              <InfoOverlay
                activeQuestion={activeQuestion}
                activeReportReviewComments={activeReportReviewComments}
                activeReportReviewNote={activeReportReviewNote}
                addNote={addNote}
                datasheetCurrentVersionId={datasheetCurrentVersionId}
                datasheetId={datasheetId}
                getResult={getResult}
                getResultAttachments={getResultAttachments}
                getResultExternalAttachments={getResultExternalAttachments}
                hasSaqService={hasSaqService}
                hasUnverifiedSaqService={hasUnverifiedSaqService}
                isCreateNote={isCreateNote}
                isInfoCollapsed={isInfoCollapsed}
                isReportReviewer={isReportReviewer}
                isSidebarExpanded={isSidebarExpanded}
                isUnderReviewing={isUnderReviewing}
                jobId={job.id}
                onDeletingExternalAttachment={onDeletingExternalAttachment}
                readonly={readonly}
                refetchReportReview={refetchReportReview}
                resultHistories={resultHistories}
                setIsCreateNote={setIsCreateNote}
                setIsInfoCollapsed={setIsInfoCollapsed}
                setResult={setResult}
                setShowNote={setShowNote}
                setShowResult={setShowResult}
                showHistory={showHistory}
                showNote={showNote}
                showResult={showResult}
                isInternalUser={isInternalUser}
              />
            ))}
          </Fragment>
        )}
        {!isInternalUser && job.status === UNDER_EXTERNAL_SAQ &&
          <ExternalUserRightSideSection
            activeQuestion={activeQuestion}
            activeReportReviewComments={activeReportReviewComments}
            activeReportReviewNote={activeReportReviewNote}
            datasheetCurrentVersionId={datasheetCurrentVersionId}
            datasheetId={datasheetId}
            getResult={getResult}
            getResultExternalAttachments={getResultExternalAttachments}
            hasOpenNoteForExternalUser={hasOpenNoteForExternalUser}
            hasSaqService={hasSaqService}
            hasUnverifiedSaqService={hasUnverifiedSaqService}
            isInfoCollapsed={isInfoCollapsed}
            isReportReviewer={isReportReviewer}
            isSidebarExpanded={isSidebarExpanded}
            isUnderReviewing={isUnderReviewing}
            job={job}
            onDeletingExternalAttachment={onDeletingExternalAttachment}
            readonly={readonly}
            refetchReportReview={refetchReportReview}
            setIsInfoCollapsed={setIsInfoCollapsed}
            setResult={setResult}
            setShowHistory={setShowHistory}
            setShowNote={setShowNote}
            setShowResult={setShowResult}
            setViewingExternalUserMoreInfo={setViewingExternalUserMoreInfo}
            showNote={showNote}
            showResult={showResult}
            userIsAuthorizedExternalSaqUser={userIsAuthorizedExternalSaqUser}
            viewingExternalUserMoreInfo={viewingExternalUserMoreInfo} />}
      </s.Wrapper>
    </JobDatasheetLanguageProvider>
  );

  return loading ? <Loader /> : content;
};

export const JobDatasheetForm = React.memo(withJobContext(JobDatasheetFormComponent));

JobDatasheetFormComponent.propTypes = FormPropTypes;
JobDatasheetFormComponent.defaultProps = {
  isQuesPresentInMultipleDatasheets: noop,
};
